import {
  HTMLAttributes,
  FC,
  useState,
  useEffect,
  useRef,
  useSyncExternalStore,
} from "react";
import { useLocation, useNavigationType, useNavigate } from "react-router-dom";
import clsx from "classnames";
import { useQuery, useMutation } from "@apollo/client";
import { useStyles } from "./NavigationBar.style";
import { Typography } from "../../core/Typography/Typography";
import { TypographyVariant } from "../../../constants/typography";
import { useTranslation } from "react-i18next";
import { useNavigationBar } from "../../../hooks/useNavigationBar";
import { NavItem } from "../../../types/navigationBar";
import ArrowWrapper from "../ArrowWrapper/ArrowWrapper";
import { PageRoutes } from "../../../constants/routes";
import { StaticImage } from "../../core/StaticImage/StaticImage";
import { Image } from "../../core/Image/Image";
import { Icon } from "../../core/Icon/Icon";
import {
  getActiveElementSnapshot,
  subscribeActiveElement,
  useContentActive,
} from "../../../contexts/GamepadProvider";
import { AnalyticsGA4 } from "../../../models/analyticsGA4";
import {
  Game,
  GameSession,
  GameQueueStatusEnum,
  Mutation,
} from "@onmo/graphql/graphql";
import { gameQueueStatusGQL, abandonGameQueueSlotGQL } from "../../../graphql";
import { animatedScrollIntoView } from "../../../utils/smoothScroll";
import { GameQueuePopup } from "../GameQueuePopup";
import { requestFullscreenApp } from "../../../models/screen";
import { getGameTitle } from "../../../models/game";
import { getPageByPath, NavigationMd } from "../../../models/navigation";
import { GAME_QUEUE_READY_TIMEOUT } from "../../../constants/gamePlay";
import { useAppSelector } from "../../hooks";
import { selectIsAuthenticated } from "@onmo/auth";
import { ProfileSwitcher } from "../ProfileSwitcher";

type NavigationBarProps = HTMLAttributes<HTMLDivElement>;

const POLL_GAME_QUEUE_STATUS_INTERVAL = 30_000;

type GameQueuePopupType = "up" | "expire";

// Map path to navbar type
const pathToType: Record<string, "console" | "essentials" | "byog"> = {
  [PageRoutes.Home]: "console",
  [PageRoutes.EssentialPass]: "essentials",
  [PageRoutes.InstallGame]: "byog",
};

export const NavigationBar: FC<NavigationBarProps> = () => {
  const classes = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const { t } = useTranslation();
  const navigationBar = useNavigationBar();
  const [selectedItem, setSelectedItem] = useState(location.pathname);
  const isContentActive = useContentActive();
  const isProfileSwitcherActive = useSyncExternalStore(
    subscribeActiveElement,
    () =>
      !!getActiveElementSnapshot()
        ?.getAttribute("data-gamepad")
        ?.includes("profile-switcher"),
  );
  const [isProfileSwitcherOpen, setIsProfileSwitcherOpen] = useState(false);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const moveMenuTimeRef = useRef(0);
  const { data: dataQueue } = useQuery(gameQueueStatusGQL, {
    pollInterval: POLL_GAME_QUEUE_STATUS_INTERVAL,
  });
  const [abandonGameQueueSlotMutation] = useMutation<Mutation>(
    abandonGameQueueSlotGQL,
  );
  const prevQueueStatusRef = useRef<GameQueueStatusEnum | undefined>(undefined);
  const [readyGame, setReadyGame] = useState<Game | undefined>(undefined);
  const [queuePopupType, setQueuePopupType] = useState<
    GameQueuePopupType | undefined
  >(undefined);

  const handleSelectedItem = (item: NavItem) => {
    item?.onAction?.();
    setSelectedItem(item.path);
    moveMenuTimeRef.current = Date.now();
    if (item.game) {
      const getGameType = (category: string): "essentials" | "byog" => {
        const parsed: string[] = JSON.parse(category);
        return parsed.includes("BYOG") ? "byog" : "essentials";
      };
      AnalyticsGA4.gameSelect(
        "recent",
        getGameType(
          (item?.game as Game)
            ? (item?.game as Game)?.category
            : (item?.game as GameSession).game.category,
        ),
        (item?.game as Game)?.title,
      );
    }
  };

  const onStartGame = () => {
    if (!readyGame) return;
    setQueuePopupType(undefined);
    navigate(`${PageRoutes.GamePlay}/${readyGame?.id}`, {
      state: {
        gameTitle: getGameTitle(readyGame),
        gameOrientation: readyGame?.orientation,
      },
    });
    requestFullscreenApp();
  };

  const onAbandonGameQueue = async () => {
    if (!readyGame) return;
    try {
      await abandonGameQueueSlotMutation({
        variables: { input: { gameId: readyGame.id } },
      });
      setQueuePopupType(undefined);
    } catch (error) {
      console.error(error);
    }
  };

  const onCloseQueuePopup = async () => {
    if (queuePopupType === "up") {
      await onAbandonGameQueue();
    } else {
      setQueuePopupType(undefined);
    }
  };

  // Expire the held slot if the user leaves "You're Up!" open without acting.
  // The cleanup cancels the timer whenever the popup is closed or starts a game.
  useEffect(() => {
    if (queuePopupType !== "up") return;

    const timeoutId = setTimeout(
      () => setQueuePopupType("expire"),
      GAME_QUEUE_READY_TIMEOUT,
    );
    return () => clearTimeout(timeoutId);
  }, [queuePopupType]);

  // Detect QUEUED -> COMPLETED transition on each poll and surface the popup
  useEffect(() => {
    const status = dataQueue?.gameQueueStatus?.status;
    const prevStatus = prevQueueStatusRef.current;
    prevQueueStatusRef.current = status;

    if (status === GameQueueStatusEnum.Queued) {
      setReadyGame(dataQueue?.gameQueueStatus?.game);
    }
    if (
      prevStatus === GameQueueStatusEnum.Queued &&
      status === GameQueueStatusEnum.Completed
    ) {
      setQueuePopupType("up");
    }
    // Only react to a change in queue status; navigationBar is looked up lazily
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataQueue?.gameQueueStatus?.status]);

  useEffect(() => {
    // Skip updating selected item if the menu was moved
    if (Date.now() - moveMenuTimeRef.current > 500) {
      setSelectedItem(location.pathname);
      moveMenuTimeRef.current = Date.now();
    }
  }, [location.pathname]);

  useEffect(() => {
    const type = pathToType[location.pathname];
    if (type) {
      AnalyticsGA4.navbarAction(type);
    }

    // Scroll the selected menu item into view
    const shouldAnimate = navigationType === "PUSH";

    const rafId = requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(
        `[data-gamepad="menu-${location.pathname}"]`,
      );
      if (!el) return;
      if (!shouldAnimate) {
        el.scrollIntoView({
          block: "nearest",
          inline: "start",
          behavior: "instant",
        });
      } else {
        animatedScrollIntoView(el, {
          axis: "x",
          block: "nearest",
          inline: "start",
        });
      }
    });
    return () => cancelAnimationFrame(rafId);
  }, [location.pathname, navigationType]);

  return (
    <div
      className={clsx(classes.navigationBar, classes.notAtTop)}
      id="navigation-bar"
    >
      {/* main navigation bar */}
      <ArrowWrapper size={20} className={classes.navInner}>
        {navigationBar.map((item, idx) => {
          const isGameLastActive = item.game?.__typename === "GameSession";
          const currentMenu = selectedItem === item.path;
          const isSetting = item.id === "icon-setting";

          return item.game ? (
            <div
              key={item.path + idx}
              className={clsx(
                classes.imgGame,
                selectedItem !== item.path && classes.hover,
                { [classes.itemSelectedMain]: currentMenu && !isContentActive },
              )}
              data-selected-class={classes.itemSelected}
              data-gamepad={`menu-${item.path}`}
              role="button"
              tabIndex={-1}
              aria-current={currentMenu ? "page" : undefined}
              aria-label={getGameTitle(
                NavigationMd.isGameSession(item.game)
                  ? item.game.game
                  : item.game,
              )}
              onClick={() => handleSelectedItem(item)}
            >
              <Image
                src={item.image?.src1x}
                src2x={item.image?.src2x}
                src3x={item.image?.src3x}
                alt=""
                className={classes.imgGame}
              />
              {isGameLastActive && (
                <div className={classes.gameLastActiveDot} />
              )}
              {dataQueue?.gameQueueStatus?.game?.id === item?.game?.id && (
                <div className={classes.hourGlassBadge}>
                  <Icon name="icon-hour-glass" size={16} />
                </div>
              )}
            </div>
          ) : (
            <div
              key={item.path + idx}
              className={clsx(
                !isSetting && item.image && classes.content,
                !currentMenu && classes.hover,
                item.icon && classes.icon,
                currentMenu && !isContentActive && classes.itemSelectedMain,
                currentMenu &&
                  isContentActive &&
                  !isProfileSwitcherActive &&
                  !isProfileSwitcherOpen &&
                  classes.itemSelectedMainScrolled,
              )}
              data-gamepad={`menu-${item.path}`}
              data-selected-class={classes.itemSelected}
              role="button"
              tabIndex={-1}
              aria-current={currentMenu ? "page" : undefined}
              aria-label={
                item.nameKey ? undefined : t(getPageByPath(item.path))
              }
              onClick={() => handleSelectedItem(item)}
            >
              <StaticImage
                className={clsx(
                  classes.imgPage,
                  item.icon && classes.sizeImage,
                )}
                src={item.image?.src3x}
                src2x={item.image?.src2x}
                src3x={item.image?.src3x}
                alt=""
              />
              {item.nameKey && (
                <Typography
                  variant={TypographyVariant.BodyB3Medium}
                  className={classes.tabName}
                >
                  {t(item.nameKey)}
                </Typography>
              )}
            </div>
          );
        })}
        {isAuthenticated && (
          <ProfileSwitcher
            triggerClassName={classes.hover}
            triggerSelectedClassName={classes.itemSelected}
            onOpenChange={setIsProfileSwitcherOpen}
          />
        )}
      </ArrowWrapper>
      <GameQueuePopup
        open={!!queuePopupType}
        type={queuePopupType ?? "up"}
        game={readyGame}
        onClose={onCloseQueuePopup}
        onSubmit={onStartGame}
      />
    </div>
  );
};
